'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RoutingRule {
  id: string;
  rule_name: string;
  product_type: string;
  primary_supplier: string;
  fallback_suppliers: string[];
  is_active: boolean;
  priority: number;
}

interface CommerceRule {
  id: string;
  rule_name: string;
  rule_type: string;
  applies_to: string[];
  action: { type: string; value: number };
  priority: number;
  is_active: boolean;
  usage_count: number;
}

interface WorkflowDef {
  id: string;
  workflow_name: string;
  workflow_type: string;
  trigger_event: string;
  steps: { step_id: number; name: string; action: string; timeout_ms: number; on_failure: string }[];
  is_active: boolean;
  version: number;
}

interface BookingEvent {
  id: string;
  booking_ref: string;
  event_type: string;
  supplier_slug: string;
  status: string;
  processing_time_ms: number;
  created_at: string;
}

interface StreamEvent {
  id: string;
  event_type: string;
  event_category: string;
  supplier_slug: string;
  severity: string;
  booking_ref: string;
  created_at: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SUPPLIERS = [
  'viator','gadventures','bokun','ttcgroup','tourradar','crystal_cruises',
  'airiq','amadeus','duffel','sabre','travelport','ratehawk','up17','tripjack','tbo'
];

const SUPPLIER_LABELS: Record<string, string> = {
  viator: 'Viator', gadventures: 'G Adventures', bokun: 'Bokun', ttcgroup: 'TTC Group',
  tourradar: 'TourRadar', crystal_cruises: 'Crystal Cruises', airiq: 'AIRIQ',
  amadeus: 'Amadeus', duffel: 'Duffel', sabre: 'Sabre', travelport: 'Travelport',
  ratehawk: 'RateHawk', up17: 'UP17', tripjack: 'TripJack', tbo: 'TBO',
};

const PRODUCT_TYPES = ['flight','hotel','activity','tour','transfer','insurance','cruise'];

const TABS = [
  { id: 'overview', label: 'Orchestration Overview', icon: 'CpuChipIcon' },
  { id: 'routing', label: 'Supplier Routing', icon: 'ArrowsRightLeftIcon' },
  { id: 'commerce', label: 'Commerce Rules Engine', icon: 'CurrencyDollarIcon' },
  { id: 'workflow', label: 'Workflow Designer', icon: 'RectangleGroupIcon' },
  { id: 'monitor', label: 'Booking Monitor', icon: 'EyeIcon' },
  { id: 'events', label: 'Event Stream', icon: 'BoltIcon' },
];

// ─── Mock data generators ─────────────────────────────────────────────────────

function genRoutingRules(): RoutingRule[] {
  return [
    { id: '1', rule_name: 'Flight Primary – Amadeus', product_type: 'flight', primary_supplier: 'amadeus', fallback_suppliers: ['duffel','sabre','travelport','airiq'], is_active: true, priority: 10 },
    { id: '2', rule_name: 'Hotel Primary – RateHawk', product_type: 'hotel', primary_supplier: 'ratehawk', fallback_suppliers: ['up17','tripjack','tbo'], is_active: true, priority: 10 },
    { id: '3', rule_name: 'Activity Primary – Viator', product_type: 'activity', primary_supplier: 'viator', fallback_suppliers: ['bokun','gadventures'], is_active: true, priority: 10 },
    { id: '4', rule_name: 'Tour Primary – G Adventures', product_type: 'tour', primary_supplier: 'gadventures', fallback_suppliers: ['viator','ttcgroup','tourradar'], is_active: true, priority: 10 },
    { id: '5', rule_name: 'Transfer Primary – TripJack', product_type: 'transfer', primary_supplier: 'tripjack', fallback_suppliers: ['tbo','up17'], is_active: true, priority: 10 },
    { id: '6', rule_name: 'Insurance Primary – TripJack', product_type: 'insurance', primary_supplier: 'tripjack', fallback_suppliers: ['tbo'], is_active: true, priority: 10 },
    { id: '7', rule_name: 'Cruise Primary – Crystal', product_type: 'cruise', primary_supplier: 'crystal_cruises', fallback_suppliers: ['tourradar'], is_active: true, priority: 10 },
    { id: '8', rule_name: 'Flight Fallback – Duffel', product_type: 'flight', primary_supplier: 'duffel', fallback_suppliers: ['sabre','travelport'], is_active: true, priority: 20 },
  ];
}

function genCommerceRules(): CommerceRule[] {
  return [
    { id: '1', rule_name: 'Standard Markup 15%', rule_type: 'markup', applies_to: ['all'], action: { type: 'percent', value: 15 }, priority: 100, is_active: true, usage_count: 4821 },
    { id: '2', rule_name: 'Flight Markup 10%', rule_type: 'markup', applies_to: ['amadeus','duffel','sabre'], action: { type: 'percent', value: 10 }, priority: 90, is_active: true, usage_count: 1203 },
    { id: '3', rule_name: 'Hotel Markup 18%', rule_type: 'markup', applies_to: ['ratehawk','up17'], action: { type: 'percent', value: 18 }, priority: 90, is_active: true, usage_count: 987 },
    { id: '4', rule_name: 'Activity Commission 20%', rule_type: 'commission', applies_to: ['viator','bokun'], action: { type: 'percent', value: 20 }, priority: 80, is_active: true, usage_count: 2341 },
    { id: '5', rule_name: 'Corporate Discount 5%', rule_type: 'discount', applies_to: ['all'], action: { type: 'percent', value: 5 }, priority: 70, is_active: true, usage_count: 456 },
    { id: '6', rule_name: 'Platinum Member Discount 8%', rule_type: 'discount', applies_to: ['all'], action: { type: 'percent', value: 8 }, priority: 60, is_active: true, usage_count: 189 },
    { id: '7', rule_name: 'Summer Promo 12% Off', rule_type: 'promotion', applies_to: ['viator','gadventures'], action: { type: 'percent', value: 12 }, priority: 50, is_active: false, usage_count: 0 },
    { id: '8', rule_name: 'GST 18% India', rule_type: 'tax', applies_to: ['airiq','tripjack','tbo'], action: { type: 'percent', value: 18 }, priority: 200, is_active: true, usage_count: 3102 },
  ];
}

function genWorkflows(): WorkflowDef[] {
  return [
    {
      id: '1', workflow_name: 'Standard Booking Flow', workflow_type: 'booking', trigger_event: 'booking.initiated', version: 1, is_active: true,
      steps: [
        { step_id: 1, name: 'Validate Availability', action: 'check_availability', timeout_ms: 5000, on_failure: 'abort' },
        { step_id: 2, name: 'Apply Pricing Rules', action: 'apply_pricing', timeout_ms: 2000, on_failure: 'continue' },
        { step_id: 3, name: 'Create Supplier Booking', action: 'supplier_book', timeout_ms: 30000, on_failure: 'failover' },
        { step_id: 4, name: 'Process Payment', action: 'process_payment', timeout_ms: 60000, on_failure: 'abort' },
        { step_id: 5, name: 'Send Confirmation', action: 'send_confirmation', timeout_ms: 10000, on_failure: 'continue' },
        { step_id: 6, name: 'Log Event', action: 'log_event', timeout_ms: 1000, on_failure: 'continue' },
      ],
    },
    {
      id: '2', workflow_name: 'Cancellation Flow', workflow_type: 'cancellation', trigger_event: 'booking.cancel_requested', version: 1, is_active: true,
      steps: [
        { step_id: 1, name: 'Validate Cancellation Policy', action: 'check_cancellation_policy', timeout_ms: 3000, on_failure: 'abort' },
        { step_id: 2, name: 'Cancel Supplier Booking', action: 'supplier_cancel', timeout_ms: 30000, on_failure: 'manual_review' },
        { step_id: 3, name: 'Process Refund', action: 'process_refund', timeout_ms: 60000, on_failure: 'queue' },
        { step_id: 4, name: 'Notify Customer', action: 'send_cancellation_notice', timeout_ms: 5000, on_failure: 'continue' },
      ],
    },
    {
      id: '3', workflow_name: 'Amendment Flow', workflow_type: 'amendment', trigger_event: 'booking.amend_requested', version: 1, is_active: true,
      steps: [
        { step_id: 1, name: 'Check Amendment Eligibility', action: 'check_amendment_policy', timeout_ms: 3000, on_failure: 'abort' },
        { step_id: 2, name: 'Get New Availability', action: 'check_availability', timeout_ms: 5000, on_failure: 'abort' },
        { step_id: 3, name: 'Apply Amendment', action: 'supplier_amend', timeout_ms: 30000, on_failure: 'manual_review' },
        { step_id: 4, name: 'Adjust Payment', action: 'adjust_payment', timeout_ms: 30000, on_failure: 'queue' },
        { step_id: 5, name: 'Send Amendment Confirmation', action: 'send_amendment_notice', timeout_ms: 5000, on_failure: 'continue' },
      ],
    },
    {
      id: '4', workflow_name: 'Refund Flow', workflow_type: 'refund', trigger_event: 'refund.requested', version: 1, is_active: true,
      steps: [
        { step_id: 1, name: 'Validate Refund Eligibility', action: 'check_refund_policy', timeout_ms: 3000, on_failure: 'abort' },
        { step_id: 2, name: 'Initiate Supplier Refund', action: 'supplier_refund', timeout_ms: 30000, on_failure: 'manual_review' },
        { step_id: 3, name: 'Process Payment Refund', action: 'process_refund', timeout_ms: 60000, on_failure: 'queue' },
        { step_id: 4, name: 'Update Booking Status', action: 'update_booking', timeout_ms: 2000, on_failure: 'continue' },
        { step_id: 5, name: 'Notify Customer', action: 'send_refund_notice', timeout_ms: 5000, on_failure: 'continue' },
      ],
    },
  ];
}

function genBookingEvents(): BookingEvent[] {
  const types = ['search','select','hold','confirm','amend','cancel','refund','webhook'];
  const statuses = ['processed','processed','processed','failed','retrying'];
  return Array.from({ length: 20 }, (_, i) => ({
    id: `evt-${i}`,
    booking_ref: `WW-${2024000 + i}`,
    event_type: types[i % types.length],
    supplier_slug: SUPPLIERS[i % SUPPLIERS.length],
    status: statuses[i % statuses.length],
    processing_time_ms: 120 + Math.floor(Math.random() * 2000),
    created_at: new Date(Date.now() - i * 45000).toISOString(),
  }));
}

function genStreamEvents(): StreamEvent[] {
  const types = ['booking.confirmed','booking.cancelled','supplier.health_check','payment.processed','webhook.received','availability.updated','sync.completed','alert.triggered'];
  const categories = ['booking','supplier','payment','webhook','system'];
  const severities = ['info','info','info','warning','error','info','info','critical'];
  return Array.from({ length: 30 }, (_, i) => ({
    id: `stream-${i}`,
    event_type: types[i % types.length],
    event_category: categories[i % categories.length],
    supplier_slug: SUPPLIERS[i % SUPPLIERS.length],
    severity: severities[i % severities.length],
    booking_ref: `WW-${2024000 + i}`,
    created_at: new Date(Date.now() - i * 18000).toISOString(),
  }));
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color = 'primary', icon }: { label: string; value: string | number; sub?: string; color?: string; icon: string }) {
  const colorMap: Record<string, string> = {
    primary: 'text-primary bg-primary/10 border-primary/20',
    emerald: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    amber: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
    red: 'text-red-400 bg-red-400/10 border-red-400/20',
    blue: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    violet: 'text-violet-400 bg-violet-400/10 border-violet-400/20',
  };
  return (
    <div className={`rounded-xl border p-4 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={16} />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function Badge({ text, color }: { text: string; color: string }) {
  const map: Record<string, string> = {
    active: 'bg-emerald-400/15 text-emerald-400',
    inactive: 'bg-zinc-500/20 text-zinc-400',
    markup: 'bg-blue-400/15 text-blue-400',
    commission: 'bg-violet-400/15 text-violet-400',
    discount: 'bg-emerald-400/15 text-emerald-400',
    tax: 'bg-amber-400/15 text-amber-400',
    promotion: 'bg-pink-400/15 text-pink-400',
    booking: 'bg-blue-400/15 text-blue-400',
    cancellation: 'bg-red-400/15 text-red-400',
    amendment: 'bg-amber-400/15 text-amber-400',
    refund: 'bg-violet-400/15 text-violet-400',
    processed: 'bg-emerald-400/15 text-emerald-400',
    failed: 'bg-red-400/15 text-red-400',
    retrying: 'bg-amber-400/15 text-amber-400',
    info: 'bg-blue-400/15 text-blue-400',
    warning: 'bg-amber-400/15 text-amber-400',
    error: 'bg-red-400/15 text-red-400',
    critical: 'bg-red-600/20 text-red-400 font-bold',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${map[color] ?? 'bg-zinc-500/20 text-zinc-400'}`}>
      {text}
    </span>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

function OverviewTab() {
  const supplierStatuses = SUPPLIERS.map((s, i) => ({
    slug: s,
    name: SUPPLIER_LABELS[s],
    status: i < 3 ? 'live' : i < 8 ? 'ready' : 'awaiting_credentials',
    products: ['flight','hotel','activity','tour','transfer','insurance','cruise'][i % 7],
    health: i < 5 ? 'healthy' : i < 10 ? 'degraded' : 'unknown',
    lastSync: i < 3 ? `${i + 1}m ago` : i < 8 ? `${i * 5}m ago` : 'N/A',
  }));

  return (
    <div className="space-y-6">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Active Suppliers" value="15" sub="All registered" color="primary" icon="BuildingStorefrontIcon" />
        <StatCard label="Live Suppliers" value="3" sub="Viator, G Adv, Crystal" color="emerald" icon="CheckCircleIcon" />
        <StatCard label="Bookings Today" value="247" sub="+12% vs yesterday" color="blue" icon="CalendarDaysIcon" />
        <StatCard label="Search Sessions" value="1,842" sub="Last 24h" color="violet" icon="MagnifyingGlassIcon" />
        <StatCard label="Failovers Today" value="4" sub="Auto-resolved" color="amber" icon="ArrowPathIcon" />
        <StatCard label="Revenue Today" value="$84.2K" sub="Across all suppliers" color="emerald" icon="BanknotesIcon" />
      </div>

      {/* Architecture Overview */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="CpuChipIcon" size={16} className="text-primary" />
          Orchestration Engine Architecture
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: 'Search Aggregation Layer', desc: 'Multi-supplier parallel search with AI-powered result ranking and deduplication', icon: 'MagnifyingGlassIcon', color: 'blue' },
            { title: 'Dynamic Package Assembly', desc: 'Flights + Hotels + Activities + Transfers + Insurance + Cruises assembled in real-time', icon: 'PuzzlePieceIcon', color: 'violet' },
            { title: 'AI Supplier Selection', desc: 'Gemini-powered supplier routing based on price, availability, SLA, and customer profile', icon: 'SparklesIcon', color: 'primary' },
            { title: 'Commerce Rules Engine', desc: 'Dynamic markups, commissions, discounts, taxes, promotions, and membership pricing', icon: 'CurrencyDollarIcon', color: 'emerald' },
            { title: 'Intelligent Failover', desc: 'Automatic supplier fallback with configurable thresholds and retry logic', icon: 'ArrowPathIcon', color: 'amber' },
            { title: 'Event-Driven Processing', desc: 'Real-time booking event streaming with webhook orchestration and queue management', icon: 'BoltIcon', color: 'red' },
          ].map((item) => (
            <div key={item.title} className="rounded-lg border border-border bg-surface-1 p-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${
                item.color === 'blue' ? 'bg-blue-400/15' : item.color === 'violet' ? 'bg-violet-400/15' :
                item.color === 'primary' ? 'bg-primary/15' : item.color === 'emerald' ? 'bg-emerald-400/15' :
                item.color === 'amber' ? 'bg-amber-400/15' : 'bg-red-400/15'
              }`}>
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className={
                  item.color === 'blue' ? 'text-blue-400' : item.color === 'violet' ? 'text-violet-400' :
                  item.color === 'primary' ? 'text-primary' : item.color === 'emerald' ? 'text-emerald-400' :
                  item.color === 'amber' ? 'text-amber-400' : 'text-red-400'
                } />
              </div>
              <h4 className="text-xs font-semibold text-foreground mb-1">{item.title}</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Supplier Fleet */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BuildingStorefrontIcon" size={16} className="text-primary" />
          Supplier Fleet Status
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                {['Supplier','Category','Status','Health','Last Sync','Failover'].map(h => (
                  <th key={h} className="text-left py-2 px-3 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {supplierStatuses.map((s) => (
                <tr key={s.slug} className="border-b border-border/50 hover:bg-surface-1 transition-colors">
                  <td className="py-2 px-3 font-medium text-foreground">{s.name}</td>
                  <td className="py-2 px-3 text-muted-foreground capitalize">{s.products}</td>
                  <td className="py-2 px-3">
                    <Badge text={s.status === 'live' ? 'Live' : s.status === 'ready' ? 'Ready' : 'Pending'} color={s.status === 'live' ? 'active' : 'inactive'} />
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center gap-1 text-xs ${s.health === 'healthy' ? 'text-emerald-400' : s.health === 'degraded' ? 'text-amber-400' : 'text-zinc-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.health === 'healthy' ? 'bg-emerald-400' : s.health === 'degraded' ? 'bg-amber-400' : 'bg-zinc-500'}`} />
                      {s.health}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">{s.lastSync}</td>
                  <td className="py-2 px-3 text-muted-foreground">
                    {s.status === 'live' ? <span className="text-emerald-400">Enabled</span> : <span className="text-zinc-500">N/A</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Package Assembly Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-surface-2 p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="PuzzlePieceIcon" size={16} className="text-violet-400" />
            Dynamic Package Assembly
          </h3>
          <div className="space-y-3">
            {[
              { type: 'Flights + Hotels', count: 142, pct: 58 },
              { type: 'Full Package (All)', count: 67, pct: 27 },
              { type: 'Tours + Transfers', count: 28, pct: 11 },
              { type: 'Cruise + Flights', count: 10, pct: 4 },
            ].map((p) => (
              <div key={p.type}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{p.type}</span>
                  <span className="text-foreground font-medium">{p.count} packages</span>
                </div>
                <div className="h-1.5 bg-surface-1 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-400 rounded-full" style={{ width: `${p.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-surface-2 p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="SparklesIcon" size={16} className="text-primary" />
            AI Concierge Orchestration
          </h3>
          <div className="space-y-3">
            {[
              { label: 'AI Supplier Selections Today', value: '1,204', icon: 'CpuChipIcon', color: 'text-primary' },
              { label: 'Avg AI Response Time', value: '1.2s', icon: 'ClockIcon', color: 'text-emerald-400' },
              { label: 'AI Failover Triggers', value: '4', icon: 'ArrowPathIcon', color: 'text-amber-400' },
              { label: 'Itinerary Optimizations', value: '89', icon: 'MapIcon', color: 'text-blue-400' },
              { label: 'Cross-Supplier Packages', value: '247', icon: 'PuzzlePieceIcon', color: 'text-violet-400' },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-border/50">
                <div className="flex items-center gap-2">
                  <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className={item.color} />
                  <span className="text-xs text-muted-foreground">{item.label}</span>
                </div>
                <span className="text-xs font-semibold text-foreground">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Supplier Routing ────────────────────────────────────────────────────

function SupplierRoutingTab() {
  const [rules, setRules] = useState<RoutingRule[]>(genRoutingRules());
  const [editId, setEditId] = useState<string | null>(null);

  const toggleActive = (id: string) => {
    setRules(prev => prev.map(r => r.id === id ? { ...r, is_active: !r.is_active } : r));
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Supplier Routing Rules</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Configure primary suppliers and automatic failover chains per product type</p>
        </div>
        <button className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
          <Icon name="PlusIcon" size={14} />
          Add Rule
        </button>
      </div>

      {/* Failover diagram */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Intelligent Failover Strategy</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { trigger: 'API Error / Timeout', action: 'Immediate failover to next supplier in chain', threshold: '< 5s', color: 'red' },
            { trigger: 'Price Threshold Exceeded', action: 'Route to lower-cost supplier alternative', threshold: 'Configurable', color: 'amber' },
            { trigger: 'Availability Zero', action: 'Cascade through fallback suppliers', threshold: 'Real-time', color: 'blue' },
          ].map((item) => (
            <div key={item.trigger} className={`rounded-lg border p-4 ${item.color === 'red' ? 'border-red-400/20 bg-red-400/5' : item.color === 'amber' ? 'border-amber-400/20 bg-amber-400/5' : 'border-blue-400/20 bg-blue-400/5'}`}>
              <div className={`text-xs font-semibold mb-1 ${item.color === 'red' ? 'text-red-400' : item.color === 'amber' ? 'text-amber-400' : 'text-blue-400'}`}>{item.trigger}</div>
              <div className="text-xs text-muted-foreground mb-2">{item.action}</div>
              <div className="text-xs text-foreground font-medium">Threshold: {item.threshold}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Rules table */}
      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-1">
              <tr>
                {['Rule Name','Product Type','Primary Supplier','Fallback Chain','Priority','Status','Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className="border-t border-border/50 hover:bg-surface-1 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground">{rule.rule_name}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs capitalize">{rule.product_type}</span>
                  </td>
                  <td className="py-3 px-4 text-foreground font-medium">{SUPPLIER_LABELS[rule.primary_supplier] ?? rule.primary_supplier}</td>
                  <td className="py-3 px-4">
                    <div className="flex flex-wrap gap-1">
                      {rule.fallback_suppliers.map((s, i) => (
                        <span key={s} className="flex items-center gap-1 text-xs text-muted-foreground">
                          {i > 0 && <Icon name="ArrowRightIcon" size={10} className="text-zinc-600" />}
                          <span className="px-1.5 py-0.5 rounded bg-surface-1 border border-border">{SUPPLIER_LABELS[s] ?? s}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">{rule.priority}</td>
                  <td className="py-3 px-4">
                    <button onClick={() => toggleActive(rule.id)} className="focus:outline-none">
                      <Badge text={rule.is_active ? 'Active' : 'Inactive'} color={rule.is_active ? 'active' : 'inactive'} />
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditId(rule.id)} className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors">
                        <Icon name="PencilIcon" size={13} />
                      </button>
                      <button className="p-1 rounded hover:bg-red-400/10 text-muted-foreground hover:text-red-400 transition-colors">
                        <Icon name="TrashIcon" size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Commerce Rules Engine ───────────────────────────────────────────────

function CommerceRulesTab() {
  const [rules, setRules] = useState<CommerceRule[]>(genCommerceRules());
  const [filterType, setFilterType] = useState('all');

  const filtered = filterType === 'all' ? rules : rules.filter(r => r.rule_type === filterType);

  const ruleTypeColor: Record<string, string> = {
    markup: 'blue', commission: 'violet', discount: 'emerald', tax: 'amber', promotion: 'pink', membership: 'primary',
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Commerce Rules Engine</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Dynamic pricing, markups, commissions, discounts, taxes, promotions, and membership rules</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-xs bg-surface-1 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none"
          >
            <option value="all">All Types</option>
            {['markup','commission','discount','tax','promotion','membership'].map(t => (
              <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
          <button className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
            <Icon name="PlusIcon" size={14} />
            Add Rule
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {['markup','commission','discount','tax','promotion','membership'].map((type) => {
          const count = rules.filter(r => r.rule_type === type).length;
          const active = rules.filter(r => r.rule_type === type && r.is_active).length;
          return (
            <button
              key={type}
              onClick={() => setFilterType(type === filterType ? 'all' : type)}
              className={`rounded-xl border p-3 text-left transition-all ${filterType === type ? 'border-primary/40 bg-primary/10' : 'border-border bg-surface-2 hover:border-primary/20'}`}
            >
              <div className="text-lg font-bold text-foreground">{count}</div>
              <div className="text-xs text-muted-foreground capitalize">{type}</div>
              <div className="text-xs text-emerald-400 mt-0.5">{active} active</div>
            </button>
          );
        })}
      </div>

      {/* Rules table */}
      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-1">
              <tr>
                {['Rule Name','Type','Applies To','Action','Priority','Usage','Status','Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((rule) => (
                <tr key={rule.id} className="border-t border-border/50 hover:bg-surface-1 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground">{rule.rule_name}</td>
                  <td className="py-3 px-4">
                    <Badge text={rule.rule_type} color={ruleTypeColor[rule.rule_type] ?? 'inactive'} />
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">
                    {rule.applies_to[0] === 'all' ? 'All Suppliers' : rule.applies_to.slice(0,2).map(s => SUPPLIER_LABELS[s] ?? s).join(', ') + (rule.applies_to.length > 2 ? ` +${rule.applies_to.length - 2}` : '')}
                  </td>
                  <td className="py-3 px-4 font-semibold text-foreground">
                    {rule.action.type === 'percent' ? `${rule.action.value}%` : `$${rule.action.value}`}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">{rule.priority}</td>
                  <td className="py-3 px-4 text-muted-foreground">{rule.usage_count.toLocaleString()}</td>
                  <td className="py-3 px-4">
                    <Badge text={rule.is_active ? 'Active' : 'Inactive'} color={rule.is_active ? 'active' : 'inactive'} />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button className="p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors">
                        <Icon name="PencilIcon" size={13} />
                      </button>
                      <button className="p-1 rounded hover:bg-red-400/10 text-muted-foreground hover:text-red-400 transition-colors">
                        <Icon name="TrashIcon" size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pricing engine info */}
      <div className="rounded-xl border border-border bg-surface-2 p-5">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">Pricing Engine Capabilities</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Multi-Currency', desc: '150+ currencies with live FX rates', icon: 'CurrencyDollarIcon' },
            { label: 'Dynamic Markups', desc: 'Per supplier, product, and customer tier', icon: 'ArrowTrendingUpIcon' },
            { label: 'Commission Engine', desc: 'Tiered commissions with auto-settlement', icon: 'BanknotesIcon' },
            { label: 'Taxation Engine', desc: 'GST, VAT, TDS per jurisdiction', icon: 'DocumentTextIcon' },
            { label: 'Coupon & Promo', desc: 'Code-based and automatic promotions', icon: 'TagIcon' },
            { label: 'Membership Pricing', desc: 'Silver, Gold, Platinum tier discounts', icon: 'StarIcon' },
            { label: 'Corporate Rates', desc: 'B2B negotiated rates and credit limits', icon: 'BuildingOfficeIcon' },
            { label: 'White-Label Pricing', desc: 'Tenant-specific markup profiles', icon: 'SwatchIcon' },
          ].map((item) => (
            <div key={item.label} className="flex items-start gap-3 p-3 rounded-lg bg-surface-1 border border-border">
              <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-xs font-semibold text-foreground">{item.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Workflow Designer ───────────────────────────────────────────────────

function WorkflowDesignerTab() {
  const [workflows, setWorkflows] = useState<WorkflowDef[]>(genWorkflows());
  const [selected, setSelected] = useState<WorkflowDef | null>(workflows[0]);

  const onFailureColor: Record<string, string> = {
    abort: 'text-red-400', continue: 'text-emerald-400', failover: 'text-amber-400',
    manual_review: 'text-violet-400', queue: 'text-blue-400',
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Workflow Designer</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Design and manage booking, cancellation, amendment, and refund orchestration workflows</p>
        </div>
        <button className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
          <Icon name="PlusIcon" size={14} />
          New Workflow
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Workflow list */}
        <div className="space-y-2">
          {workflows.map((wf) => (
            <button
              key={wf.id}
              onClick={() => setSelected(wf)}
              className={`w-full text-left rounded-xl border p-4 transition-all ${selected?.id === wf.id ? 'border-primary/40 bg-primary/10' : 'border-border bg-surface-2 hover:border-primary/20'}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-foreground">{wf.workflow_name}</span>
                <Badge text={wf.is_active ? 'Active' : 'Inactive'} color={wf.is_active ? 'active' : 'inactive'} />
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <Badge text={wf.workflow_type} color={wf.workflow_type} />
                <span>{wf.steps.length} steps</span>
                <span>v{wf.version}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">{wf.trigger_event}</div>
            </button>
          ))}
        </div>

        {/* Workflow detail */}
        {selected && (
          <div className="lg:col-span-2 rounded-xl border border-border bg-surface-2 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-sm font-semibold text-foreground">{selected.workflow_name}</h4>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">Trigger: {selected.trigger_event}</p>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors">Edit</button>
                <button className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-xs font-medium hover:bg-primary/20 transition-colors">Test Run</button>
              </div>
            </div>

            {/* Step flow */}
            <div className="space-y-2">
              {selected.steps.map((step, idx) => (
                <div key={step.step_id} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                      {step.step_id}
                    </div>
                    {idx < selected.steps.length - 1 && (
                      <div className="w-px h-6 bg-border mt-1" />
                    )}
                  </div>
                  <div className="flex-1 rounded-lg border border-border bg-surface-1 p-3 mb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-foreground">{step.name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-muted-foreground font-mono">{step.action}</span>
                        <span className="text-muted-foreground">{step.timeout_ms}ms</span>
                        <span className={`font-medium ${onFailureColor[step.on_failure] ?? 'text-muted-foreground'}`}>
                          on_fail: {step.on_failure}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Booking Monitor ─────────────────────────────────────────────────────

function BookingMonitorTab() {
  const [events, setEvents] = useState<BookingEvent[]>(genBookingEvents());
  const [filter, setFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      setEvents(genBookingEvents());
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const filtered = filter === 'all' ? events : events.filter(e => e.status === filter);

  const stats = {
    total: events.length,
    processed: events.filter(e => e.status === 'processed').length,
    failed: events.filter(e => e.status === 'failed').length,
    retrying: events.filter(e => e.status === 'retrying').length,
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Booking Orchestration Monitor</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Real-time view of all booking orchestration events across suppliers</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${autoRefresh ? 'bg-emerald-400/15 text-emerald-400' : 'border border-border text-muted-foreground hover:text-foreground'}`}
          >
            <Icon name="ArrowPathIcon" size={13} className={autoRefresh ? 'animate-spin' : ''} />
            {autoRefresh ? 'Live' : 'Auto-Refresh'}
          </button>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-xs bg-surface-1 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="processed">Processed</option>
            <option value="failed">Failed</option>
            <option value="retrying">Retrying</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Events" value={stats.total} color="primary" icon="BoltIcon" />
        <StatCard label="Processed" value={stats.processed} color="emerald" icon="CheckCircleIcon" />
        <StatCard label="Failed" value={stats.failed} color="red" icon="XCircleIcon" />
        <StatCard label="Retrying" value={stats.retrying} color="amber" icon="ArrowPathIcon" />
      </div>

      {/* Events table */}
      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-1">
              <tr>
                {['Booking Ref','Event Type','Supplier','Status','Processing Time','Timestamp'].map(h => (
                  <th key={h} className="text-left py-3 px-4 text-muted-foreground font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((evt) => (
                <tr key={evt.id} className="border-t border-border/50 hover:bg-surface-1 transition-colors">
                  <td className="py-2.5 px-4 font-mono text-primary font-medium">{evt.booking_ref}</td>
                  <td className="py-2.5 px-4">
                    <span className="px-2 py-0.5 rounded-full bg-surface-1 border border-border text-muted-foreground capitalize">{evt.event_type}</span>
                  </td>
                  <td className="py-2.5 px-4 text-foreground">{SUPPLIER_LABELS[evt.supplier_slug] ?? evt.supplier_slug}</td>
                  <td className="py-2.5 px-4">
                    <Badge text={evt.status} color={evt.status} />
                  </td>
                  <td className="py-2.5 px-4 text-muted-foreground">
                    <span className={evt.processing_time_ms > 1500 ? 'text-amber-400' : 'text-muted-foreground'}>
                      {evt.processing_time_ms}ms
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-muted-foreground">
                    {new Date(evt.created_at).toLocaleTimeString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Event Stream Monitor ────────────────────────────────────────────────

function EventStreamTab() {
  const [events, setEvents] = useState<StreamEvent[]>(genStreamEvents());
  const [paused, setPaused] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    if (paused) return;
    const interval = setInterval(() => {
      const newEvt: StreamEvent = {
        id: `stream-new-${Date.now()}`,
        event_type: ['booking.confirmed','supplier.health_check','payment.processed','webhook.received','availability.updated'][Math.floor(Math.random() * 5)],
        event_category: ['booking','supplier','payment','webhook','system'][Math.floor(Math.random() * 5)],
        supplier_slug: SUPPLIERS[Math.floor(Math.random() * SUPPLIERS.length)],
        severity: ['info','info','info','warning','error'][Math.floor(Math.random() * 5)],
        booking_ref: `WW-${2024000 + Math.floor(Math.random() * 1000)}`,
        created_at: new Date().toISOString(),
      };
      setEvents(prev => [newEvt, ...prev.slice(0, 49)]);
    }, 3000);
    return () => clearInterval(interval);
  }, [paused]);

  const filtered = events
    .filter(e => filterSeverity === 'all' || e.severity === filterSeverity)
    .filter(e => filterCategory === 'all' || e.event_category === filterCategory);

  const severityIcon: Record<string, string> = {
    info: 'InformationCircleIcon', warning: 'ExclamationTriangleIcon', error: 'XCircleIcon', critical: 'FireIcon',
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Real-Time Event Stream Monitor</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Live booking event streaming across all suppliers and system components</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPaused(v => !v)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${!paused ? 'bg-emerald-400/15 text-emerald-400' : 'border border-border text-muted-foreground hover:text-foreground'}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${!paused ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-500'}`} />
            {paused ? 'Paused' : 'Live'}
          </button>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="text-xs bg-surface-1 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none"
          >
            <option value="all">All Severity</option>
            {['info','warning','error','critical'].map(s => (
              <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="text-xs bg-surface-1 border border-border rounded-lg px-3 py-1.5 text-foreground focus:outline-none"
          >
            <option value="all">All Categories</option>
            {['booking','supplier','payment','webhook','system'].map(c => (
              <option key={c} value={c} className="capitalize">{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stream stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {['info','warning','error','critical'].map(sev => {
          const count = events.filter(e => e.severity === sev).length;
          return (
            <div key={sev} className={`rounded-xl border p-3 ${sev === 'info' ? 'border-blue-400/20 bg-blue-400/5' : sev === 'warning' ? 'border-amber-400/20 bg-amber-400/5' : sev === 'error' ? 'border-red-400/20 bg-red-400/5' : 'border-red-600/20 bg-red-600/5'}`}>
              <div className={`text-lg font-bold ${sev === 'info' ? 'text-blue-400' : sev === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>{count}</div>
              <div className="text-xs text-muted-foreground capitalize">{sev}</div>
            </div>
          );
        })}
        <div className="rounded-xl border border-border bg-surface-2 p-3">
          <div className="text-lg font-bold text-foreground">{events.length}</div>
          <div className="text-xs text-muted-foreground">Total Events</div>
        </div>
      </div>

      {/* Stream feed */}
      <div className="rounded-xl border border-border bg-surface-2 overflow-hidden">
        <div className="bg-surface-1 px-4 py-2.5 flex items-center justify-between border-b border-border">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Event Stream</span>
          <span className="text-xs text-muted-foreground">{filtered.length} events</span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {filtered.map((evt, idx) => (
            <div
              key={evt.id}
              className={`flex items-start gap-3 px-4 py-2.5 border-b border-border/40 hover:bg-surface-1 transition-colors ${idx === 0 && !paused ? 'bg-primary/5' : ''}`}
            >
              <Icon
                name={severityIcon[evt.severity] as Parameters<typeof Icon>[0]['name']}
                size={14}
                className={`mt-0.5 flex-shrink-0 ${evt.severity === 'info' ? 'text-blue-400' : evt.severity === 'warning' ? 'text-amber-400' : 'text-red-400'}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-mono text-foreground">{evt.event_type}</span>
                  <Badge text={evt.event_category} color={evt.severity} />
                  <span className="text-xs text-muted-foreground">{SUPPLIER_LABELS[evt.supplier_slug] ?? evt.supplier_slug}</span>
                  {evt.booking_ref && <span className="text-xs font-mono text-primary">{evt.booking_ref}</span>}
                </div>
              </div>
              <span className="text-xs text-muted-foreground flex-shrink-0 font-mono">
                {new Date(evt.created_at).toLocaleTimeString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function TravelOrchestrationCenterContent() {
  const [activeTab, setActiveTab] = useState('overview');

  const renderTab = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab />;
      case 'routing': return <SupplierRoutingTab />;
      case 'commerce': return <CommerceRulesTab />;
      case 'workflow': return <WorkflowDesignerTab />;
      case 'monitor': return <BookingMonitorTab />;
      case 'events': return <EventStreamTab />;
      default: return <OverviewTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-surface-2 px-6 py-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center">
                <Icon name="CpuChipIcon" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Travel Orchestration Center</h1>
                <p className="text-xs text-muted-foreground">Enterprise multi-supplier orchestration engine — 15 suppliers · AI-powered routing · Dynamic commerce</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-400/10 border border-emerald-400/20 text-emerald-400 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Orchestration Active
              </span>
              <a href="/travel-orchestration-report" className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90 transition-colors">
                <Icon name="DocumentChartBarIcon" size={14} />
                View Report
              </a>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mt-5 overflow-x-auto pb-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-white' :'text-muted-foreground hover:text-foreground hover:bg-surface-1'
                }`}
              >
                <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {renderTab()}
      </div>
    </div>
  );
}
